import React from 'react';

import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import type { AccountCategory, AccountSlugs } from 'Types/account';
import type { PropsWithRequiredChildren } from 'Types/react/react';

import AccountDateRangeFilter from '../AccountDateRangeFilter/AccountDateRangeFilter';
import AccountPurchaseCompleteMobile from '../AccountPurchaseCompleteFilters/AccountPurchaseCompleteMobile';
import AccountPurchaseCompleteStatusFilter from '../AccountPurchaseCompleteFilters/AccountPurchaseCompleteStatusFilter';
import AccountResultsCount from '../AccountResultsCount/AccountResultsCount';
import AccountResultsResetFilters from '../AccountResultsResetFilters/AccountResultsResetFilters';
import AccountSearchBar from '../AccountSearchBar/AccountSearchBar';

import AccountFiltersSwitch, { Default, PurchasesComplete } from './AccountFiltersSwitch';
import useAccountResetFilters from './useAccountResetFilters';

import styles from './AccountPageFilters.module.scss';

type PurchasesCompleteFiltersProps = PropsWithRequiredChildren & { count: number };

const PurchasesCompleteFilters: React.FC<PurchasesCompleteFiltersProps> = ({ children, count }) => {
	const {
		maxWidth: { breakpointTablet: isTabletOrMobile },
	} = useBreakpoint();
	if (isTabletOrMobile) {
		return (
			<>
				<AccountPurchaseCompleteMobile count={count} />
				{children}
			</>
		);
	}

	return (
		<>
			<div className={styles.purchasesCompleteFiltersContainer}>
				<AccountSearchBar />
				<AccountDateRangeFilter />
				<AccountPurchaseCompleteStatusFilter />
			</div>
			<div>{children}</div>
		</>
	);
};

type CountAndFiltersResetProps = Pick<VehicleListQuery, 'queryStatus' | 'isPreviousData'> & {
	count: number;
	keyword?: string;
};

const CountAndFilterReset: React.FC<CountAndFiltersResetProps> = ({ count, isPreviousData, keyword, queryStatus }) => {
	const { resetFilters, showFilterReset } = useAccountResetFilters({ queryStatus });

	return (
		<div className={styles.resultsCount}>
			<AccountResultsCount count={count} isPreviousData={isPreviousData} keyword={keyword} queryStatus={queryStatus} />
			{showFilterReset && <AccountResultsResetFilters onResetFilters={resetFilters} />}
		</div>
	);
};

type AccountPageFiltersProps = Pick<VehicleListQuery, 'queryStatus' | 'isPreviousData'> & {
	category: AccountCategory;
	count?: number;
	keyword?: string;
	slug: AccountSlugs;
};

const AccountPageFilters: React.FC<AccountPageFiltersProps> = ({
	category,
	count = 0,
	isPreviousData,
	keyword,
	queryStatus,
	slug,
}) => (
	<>
		<AccountFiltersSwitch
			category={category}
			currentCount={count}
			isPreviousData={isPreviousData}
			queryStatus={queryStatus}
			slug={slug}
		>
			<PurchasesComplete>
				<PurchasesCompleteFilters count={count}>
					<div className={styles.countContainer}>
						<CountAndFilterReset
							count={count}
							isPreviousData={isPreviousData}
							keyword={keyword}
							queryStatus={queryStatus}
						/>
					</div>
				</PurchasesCompleteFilters>
			</PurchasesComplete>
			<Default>
				<AccountSearchBar />
				<CountAndFilterReset
					count={count}
					isPreviousData={isPreviousData}
					keyword={keyword}
					queryStatus={queryStatus}
				/>
			</Default>
		</AccountFiltersSwitch>
	</>
);

export default AccountPageFilters;
